import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { SyriatelClaimDialog } from './SyriatelClaimDialog';
import { useLanguage } from '../../hooks/useLanguage';
import { useClaimSyriatelPaymentMutation, useGetBalanceQuery } from '../../store/api/balanceApi';
import { Transaction } from '../../types/api';
import { formatDate } from '../../utils/dateFormatter';

interface TransactionCardProps {
  transaction: Transaction;
}

export function TransactionCard({ transaction }: TransactionCardProps) {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [claimSyriatelPayment, { isLoading: isClaiming }] = useClaimSyriatelPaymentMutation();
  const { refetch: refetchBalance } = useGetBalanceQuery();

  // Check if this is a pending Syriatel recharge transaction
  const isSyriatelPendingRecharge =
    transaction.type === 'recharge' &&
    transaction.paymentMethod === 'Syriatel Cash' &&
    transaction.status === 'pending';

  // Extract invoiceId from paymentDetails or use transaction._id as fallback
  // The invoiceId should be stored in paymentDetails.invoiceId when the transaction is created
  const invoiceId = (transaction.paymentDetails?.invoiceId as string | undefined) || 
                    (transaction.paymentDetails?.internalOrderId as string | undefined) ||
                    transaction._id;

  const handleClaimSyriatelPayment = async (transactionId: string) => {
    if (!invoiceId) {
      toast.error(t('recharge.syriatelClaim.error'));
      return;
    }

    try {
      const result = await claimSyriatelPayment({
        internalOrderId: invoiceId,
        transactionId,
      }).unwrap();

      if (result.data.claimed) {
        toast.success(t('recharge.syriatelClaim.success', { amount: result.data.amountInUSD?.toFixed(2) }));
        void refetchBalance();
      } else {
        toast.info(t('recharge.syriatelClaim.pending', { message: result.data.message }));
      }
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('recharge.syriatelClaim.error')
      );
      throw err;
    }
  };

  const getTransactionTypeLabel = (type: Transaction['type']): string => {
    const typeLabels: Record<Transaction['type'], string> = {
      recharge: t('transactions.types.recharge'),
      purchase: t('transactions.types.purchase'),
      referral_bonus: t('transactions.types.referralBonus'),
      refund: t('transactions.types.refund'),
      admin_add: t('transactions.types.adminAdd'),
      admin_deduct: t('transactions.types.adminDeduct'),
      cashback_claim: t('transactions.types.cashbackClaim'),
      cashback_earn: t('transactions.types.cashbackEarn'),
      admin_add_referral: t('transactions.types.adminAddReferral'),
      admin_deduct_referral: t('transactions.types.adminDeductReferral'),
      admin_add_cashback: t('transactions.types.adminAddCashback'),
      admin_deduct_cashback: t('transactions.types.adminDeductCashback'),
    };
    return typeLabels[type] ?? t('transactions.types.recharge');
  };

  const getStatusColor = (status: Transaction['status']): string => {
    const statusColors: Record<Transaction['status'], string> = {
      pending: 'bg-yellow-500/20 text-yellow-500',
      completed: 'bg-green-500/20 text-green-500',
      failed: 'bg-danger/20 text-danger',
      reversed: 'bg-neutral-medium/20 text-neutral-medium',
    };
    return statusColors[status] || 'bg-neutral-dark text-neutral-medium';
  };

  const getStatusLabel = (status: Transaction['status']): string => {
    const statusLabels: Record<Transaction['status'], string> = {
      pending: t('transactions.status.pending'),
      completed: t('transactions.status.completed'),
      failed: t('transactions.status.failed'),
      reversed: t('transactions.status.reversed'),
    };
    return statusLabels[status] || status;
  };

  // Determine if transaction is positive (adds to balance) or negative (deducts from balance)
  const isPositiveTransaction = (type: Transaction['type']): boolean => {
    const positiveTypes: Transaction['type'][] = [
      'recharge',
      'referral_bonus',
      'cashback_claim',
      'cashback_earn',
      'refund',
      'admin_add',
      'admin_add_referral',
      'admin_add_cashback',
    ];
    return positiveTypes.includes(type);
  };

  const isPositive = isPositiveTransaction(transaction.type);
  const amountColor = isPositive ? 'text-green-500' : 'text-danger';

  return (
    <>
      <div className="card-glass rounded-lg p-4 hover:border-brand-accent/50 transition-all duration-300">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Left Side - Transaction Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-heading font-semibold text-neutral-light">
                {getTransactionTypeLabel(transaction.type)}
              </h3>
              <span className={`px-2 py-1 rounded-full text-xs font-body ${getStatusColor(transaction.status)}`}>
                {getStatusLabel(transaction.status)}
              </span>
            </div>
            
            {transaction.paymentMethod && (
              <p className="text-sm text-neutral-medium font-body mb-1">
                {t('transactions.paymentMethod')}: {transaction.paymentMethod}
              </p>
            )}
            
            {transaction.notes && (
              <p className="text-sm text-neutral-medium font-body">
                <span className="font-medium text-neutral-light">{t('transactions.notes')}:</span> {transaction.notes}
              </p>
            )}
            
            <p className="text-xs text-neutral-medium font-body mt-2">
              {formatDate(transaction.createdAt, 'PP h:mm a', currentLanguage)}
            </p>
          </div>

          {/* Right Side - Amount */}
          <div className="text-left md:text-right">
            <p className={`text-2xl font-bold font-heading ${amountColor}`}>
              {isPositive ? '+' : ''}{transaction.amount.toFixed(2)} {transaction.currency}
            </p>
          </div>
        </div>

        {/* Syriatel Claim Button - Only for pending Syriatel recharge transactions */}
        {isSyriatelPendingRecharge && invoiceId && (
          <div className="mt-4 pt-4 border-t border-neutral-dark">
            <button
              onClick={() => setShowClaimDialog(true)}
              className="w-full px-4 py-2 bg-green-500/20 text-green-400 font-heading font-semibold rounded-lg hover:bg-green-500/30 border border-green-500/30 transform hover:scale-105 transition-all duration-300 text-sm"
            >
              {t('recharge.syriatelClaim.button')}
            </button>
          </div>
        )}
      </div>

      {/* Syriatel Claim Dialog */}
      {isSyriatelPendingRecharge && invoiceId && (
        <SyriatelClaimDialog
          isOpen={showClaimDialog}
          onClose={() => setShowClaimDialog(false)}
          onClaim={handleClaimSyriatelPayment}
          isLoading={isClaiming}
          invoiceId={invoiceId}
        />
      )}
    </>
  );
}

